import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  CircularProgress
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { makeStyles } from '@material-ui/core/styles';
import axios from 'axios';

const useStyles = makeStyles(theme => ({
  dialogContent: {
    padding: theme.spacing(3),
    minWidth: 500
  },
  fieldGroup: {
    marginBottom: theme.spacing(2)
  },
  timeFields: {
    display: 'flex',
    gap: theme.spacing(2),
    alignItems: 'center'
  },
  notesField: {
    marginTop: theme.spacing(2)
  }
}));

const RestrictedAreaDialog = ({ open, onClose, onSave, editingArea = null, url, clientId }) => {
  const classes = useStyles();
  const [contacts, setContacts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  console.log('RestrictedAreaDialog....', clientId)
  const [formData, setFormData] = useState({
    name: '',
    restrictionDetails: {
      contactName: '',
      contactPhone: '',
      keyRequired: false,
      accessHours: {
        start: '',
        end: ''
      },
      accessDays: {
        monday: true,
        tuesday: true,
        wednesday: true,
        thursday: true,
        friday: true,
        saturday: true,
        sunday: true
      },
      accessNotes: '',
      restrictionType: 'appointment'
    },
    paths: []
  });

  // Fetch contacts when dialog opens
  useEffect(() => {
    const fetchContacts = async () => {
      console.log('Fetch Contacts...', open, clientId)
      if (open && clientId && url) {
        setLoadingContacts(true);
        try {
          console.log('Fetching contacts for client:', clientId);
          const response = await axios.get(`${url}/api/customer/getContacts?id=${clientId}`);
          console.log('Contacts response:', response.data);

          // Handle different response formats
          const contactList = Array.isArray(response.data)
            ? response.data
            : (response.data.contacts || response.data.data || []);

          setContacts(contactList);
        } catch (error) {
          console.error('Error fetching contacts:', error);
          setContacts([]);
        } finally {
          setLoadingContacts(false);
        }
      } else if (open && !clientId) {
        console.warn('No client ID provided to fetch contacts');
        setContacts([]);
      }
    };

    fetchContacts();
  }, [open, clientId, url]);

  useEffect(() => {
    if (editingArea) {
      console.log('Editing area data:', editingArea);
      setFormData({
        name: editingArea.name || '',
        restrictionDetails: {
          contactName: editingArea.restrictionDetails?.contactName || '',
          contactPhone: editingArea.restrictionDetails?.contactPhone || '',
          keyRequired: editingArea.restrictionDetails?.keyRequired || false,
          accessHours: {
            start: editingArea.restrictionDetails?.accessHours?.start || '',
            end: editingArea.restrictionDetails?.accessHours?.end || ''
          },
          accessDays: editingArea.restrictionDetails?.accessDays || {
            monday: true,
            tuesday: true,
            wednesday: true,
            thursday: true,
            friday: true,
            saturday: true,
            sunday: true
          },
          restrictionType: editingArea.restrictionDetails?.restrictionType || 'appointment',
          accessNotes: editingArea.restrictionDetails?.accessNotes || ''
        }
      });

      // Set selected contact if editing
      if (editingArea.restrictionDetails?.contactName) {
        // Try to find the contact in the loaded contacts list
        if (contacts.length > 0) {
          // Try to match by full name or by combining first and last name
          const existingContact = contacts.find(c => {
            const fullName = `${c.firstName} ${c.lastName}`.trim();
            return fullName === editingArea.restrictionDetails.contactName ||
                   c.name === editingArea.restrictionDetails.contactName;
          });

          if (existingContact) {
            console.log('Found matching contact:', existingContact);
            setSelectedContact(existingContact);
          } else {
            // Create a temporary contact object if not found in list
            // Parse the name if it contains space
            const nameParts = editingArea.restrictionDetails.contactName.split(' ');
            const tempContact = {
              firstName: nameParts[0] || editingArea.restrictionDetails.contactName,
              lastName: nameParts.slice(1).join(' ') || '',
              phoneNumbers: editingArea.restrictionDetails.contactPhone ?
                [{number: editingArea.restrictionDetails.contactPhone}] : []
            };
            console.log('Created temp contact:', tempContact);
            setSelectedContact(tempContact);
          }
        } else {
          // Contacts not loaded yet, create temp contact
          const nameParts = editingArea.restrictionDetails.contactName.split(' ');
          setSelectedContact({
            firstName: nameParts[0] || editingArea.restrictionDetails.contactName,
            lastName: nameParts.slice(1).join(' ') || '',
            phoneNumbers: editingArea.restrictionDetails.contactPhone ?
              [{number: editingArea.restrictionDetails.contactPhone}] : []
          });
        }
      }
    } else {
      // Reset form for new area
      setFormData({
        name: '',
        restrictionDetails: {
          contactName: '',
          contactPhone: '',
          keyRequired: false,
          accessHours: {
            start: '',
            end: ''
          },
          accessDays: {
            monday: true,
            tuesday: true,
            wednesday: true,
            thursday: true,
            friday: true,
            saturday: true,
            sunday: true
          },
          accessNotes: '',
          restrictionType: 'appointment'
        },
        paths: []
      });
      setSelectedContact(null);
    }
  }, [editingArea, open, contacts]);

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleDetailsChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      restrictionDetails: {
        ...prev.restrictionDetails,
        [field]: value
      }
    }));
  };

  const handleDayToggle = (day) => {
    setFormData(prev => ({
      ...prev,
      restrictionDetails: {
        ...prev.restrictionDetails,
        accessDays: {
          ...prev.restrictionDetails.accessDays,
          [day]: !prev.restrictionDetails.accessDays[day]
        }
      }
    }));
  };

  const handleSubmit = () => {
    onSave(formData);
    onClose();
  };


  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {editingArea ? 'Edit Restricted Area' : 'Add Restricted Area Details'}
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>
        <Grid container spacing={2}>
          {/* Area Name */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Area Name"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              placeholder="e.g., Pool Equipment Area, Backyard Gate"
              variant="outlined"
              required
            />
          </Grid>

          {/* Restriction Type */}
          <Grid item xs={12}>
            <FormControl fullWidth variant="outlined">
              <InputLabel>Restriction Level</InputLabel>
              <Select
                value={formData.restrictionDetails.restrictionType}
                onChange={(e) => handleDetailsChange('restrictionType', e.target.value)}
                label="Restriction Level"
              >
                <MenuItem value="appointment">Restricted (Appointment Required)</MenuItem>
                <MenuItem value="time-based">Time Based (Request Access On-Site)</MenuItem>
                <MenuItem value="key-required">Key Required</MenuItem>
                <MenuItem value="no-access">No Access</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Contact Information */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom>
              Contact Information
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Autocomplete
              value={selectedContact}
              onChange={(event, newValue) => {
                setSelectedContact(newValue);
                if (newValue) {
                  // Create full name from firstName and lastName
                  const fullName = `${newValue.firstName || ''} ${newValue.lastName || ''}`.trim() ||
                                   newValue.name || '';
                  handleDetailsChange('contactName', fullName);
                  // Get phone number from phoneNumbers array or fallback fields
                  const phoneNumber = newValue.phoneNumbers?.[0]?.number ||
                                     newValue.phone ||
                                     newValue.phoneNumber ||
                                     newValue.mobileNumber || '';
                  handleDetailsChange('contactPhone', phoneNumber);
                } else {
                  handleDetailsChange('contactName', '');
                  handleDetailsChange('contactPhone', '');
                }
              }}
              options={contacts}
              getOptionLabel={(option) => `${option?.firstName} ${option?.lastName}` || ''}
              renderOption={(option) => {
                const phoneNumber = option?.phoneNumbers?.length ? `${option.phoneNumbers[0]['number']}` : ''
                console.log('contact num', option)
                return (
                  <div>
                    <Typography variant="body1">{option.firstName} {option.lastName}</Typography>
                    {phoneNumber && (
                      <Typography variant="caption" color="textSecondary">
                        {phoneNumber}
                      </Typography>
                    )}
                  </div>
                );
              }}
              loading={loadingContacts}
              fullWidth
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Contact"
                  variant="outlined"
                  placeholder="Choose a contact..."
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingContacts ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Grid>

          {/* Display selected contact's phone number */}
          {selectedContact && formData.restrictionDetails.contactPhone && (
            <Grid item xs={12}>
              <Typography variant="body2" color="textSecondary">
                Contact Phone: {formData.restrictionDetails.contactPhone}
              </Typography>
            </Grid>
          )}

          {/* Only show time/days fields if restriction type is time-based */}
          {formData.restrictionDetails.restrictionType === 'time-based' && (
            <>
              {/* Access Hours */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>
                  Access Hours
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <div className={classes.timeFields}>
                  <TextField
                    label="Start Time"
                    type="time"
                    value={formData.restrictionDetails.accessHours.start}
                    onChange={(e) => handleDetailsChange('accessHours', {
                      ...formData.restrictionDetails.accessHours,
                      start: e.target.value
                    })}
                    InputLabelProps={{ shrink: true }}
                    variant="outlined"
                  />
                  <Typography>to</Typography>
                  <TextField
                    label="End Time"
                    type="time"
                    value={formData.restrictionDetails.accessHours.end}
                    onChange={(e) => handleDetailsChange('accessHours', {
                      ...formData.restrictionDetails.accessHours,
                      end: e.target.value
                    })}
                    InputLabelProps={{ shrink: true }}
                    variant="outlined"
                  />
                </div>
              </Grid>

              {/* Access Days */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>
                  Days with Time-Based Access
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={1}>
                  {Object.entries(formData.restrictionDetails.accessDays).map(([day, checked]) => (
                    <FormControlLabel
                      key={day}
                      control={
                        <Checkbox
                          checked={checked}
                          onChange={() => handleDayToggle(day)}
                          size="small"
                          color="primary"
                        />
                      }
                      label={day.charAt(0).toUpperCase() + day.slice(1)}
                    />
                  ))}
                </Box>
              </Grid>
            </>
          )}

          {/* Notes */}
          <Grid item xs={12} className={classes.notesField}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Access Notes / Special Instructions"
              value={formData.restrictionDetails.accessNotes}
              onChange={(e) => handleDetailsChange('accessNotes', e.target.value)}
              placeholder="e.g., Call 30 minutes before arrival, Dog in backyard, Gate code is #1234"
              variant="outlined"
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="default">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          disabled={!formData.name}
        >
          {editingArea ? 'Update' : 'Save'} Restricted Area
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RestrictedAreaDialog;